import * as React from 'react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../chrome/AppShell';
import useUserOrg from '../../../Authentication/components/hooks/useUserOrg';
import { useDeviceGroupList } from '../../../DeviceManagement/components/hooks/useDeviceGroupList';
import { useDeviceInvite } from '../../../DeviceManagement/components/hooks/useDeviceInvite';
import { Crumb, Field, SectionCard, Select, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { canGoBack } from './shared';
import './devices.css';

/* Standalone "Invite device" (linked from the Devices › Groups tab). */

function InviteForm({
    orgId,
    onDone,
    onRetry,
}: {
    orgId: number;
    onDone: () => void;
    onRetry: () => void;
}) {
    const { deviceGroups, isLoading, error: groupsError } = useDeviceGroupList(orgId);
    const { createDeviceInvite, error: serverError } = useDeviceInvite(orgId);
    const [name, setName] = useState('');
    const [target, setTarget] = useState('0');
    const [nameError, setNameError] = useState('');
    const [groupError, setGroupError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const busy = useRef(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy.current) return;
        const trimmed = name.trim();
        const to = Number(target);
        let bad = false;
        if (!trimmed) {
            setNameError('Give the invite a name.');
            bad = true;
        }
        if (!to) {
            setGroupError('Choose a group.');
            bad = true;
        }
        if (bad) return;
        busy.current = true;
        setSaving(true);
        setSaveError('');
        let ok: unknown;
        try {
            ok = await createDeviceInvite({ name: trimmed, groupId: to });
        } catch {
            ok = undefined;
        }
        busy.current = false;
        setSaving(false);
        if (ok) onDone();
        else setSaveError('Couldn’t create the invite. Try again.');
    };

    if (isLoading)
        return (
            <TCard>
                <TSkeleton rows={2} />
            </TCard>
        );
    if (groupsError)
        return (
            <TCard>
                <TEmpty message="Couldn’t load device groups." actionLabel="Retry" onAction={onRetry} />
            </TCard>
        );
    if (deviceGroups.length === 0)
        return (
            <TCard>
                <TEmpty message="Invites join a device group. Create a group first." />
            </TCard>
        );

    return (
        <form onSubmit={submit} noValidate>
            <SectionCard title="Invite">
                <Field
                    label="Invite name"
                    help="Enter this on the device when you register it. Each name works once."
                    error={nameError}
                >
                    <TextInput
                        autoFocus
                        value={name}
                        disabled={saving}
                        aria-invalid={!!nameError}
                        onChange={(e) => {
                            setName(e.target.value);
                            setNameError('');
                        }}
                    />
                </Field>
                <Field label="Device group" error={groupError}>
                    <Select
                        value={target}
                        disabled={saving}
                        aria-invalid={!!groupError}
                        onChange={(e) => {
                            setTarget(e.target.value);
                            setGroupError('');
                        }}
                    >
                        <option value="0">Choose a group</option>
                        {deviceGroups.map((g) => (
                            <option key={g.id} value={String(g.id)}>
                                {g.name}
                            </option>
                        ))}
                    </Select>
                </Field>
            </SectionCard>
            {(serverError || saveError) && (
                <p className="banner dv-note err" role="alert" style={{ marginTop: 16 }}>
                    {serverError || saveError}
                </p>
            )}
            <div className="dv-actions">
                <button type="button" className="tb-btn" disabled={saving} onClick={onDone}>
                    Cancel
                </button>
                <button type="submit" className="tb-new" disabled={saving}>
                    {saving ? 'Creating…' : 'Create invite'}
                </button>
            </div>
        </form>
    );
}

export default function DeviceInviteCreateV3() {
    const navigate = useNavigate();
    const { orgId } = useUserOrg();
    const [attempt, setAttempt] = useState(0);
    const leave = () =>
        canGoBack() ? navigate(-1) : navigate('/device-management#groups');

    return (
        <AppShell>
            <div className="dv-page dets">
                <Crumb label="Devices" onClick={() => navigate('/device-management#groups')} />
                <PageHeadActions
                    title="Invite a device"
                    sub="The device joins the group when it registers with this name."
                />
                {orgId ? (
                    <InviteForm
                        key={attempt}
                        orgId={orgId}
                        onDone={leave}
                        onRetry={() => setAttempt((n) => n + 1)}
                    />
                ) : (
                    <TCard>
                        <TSkeleton rows={2} />
                    </TCard>
                )}
            </div>
        </AppShell>
    );
}
